/**
 * Overview's scope picker: the shared control, wired to this page (BON-534,
 * generalised in BON-538).
 *
 * The picker is a primitive because the Venues page wants the same control —
 * the same dropdown, the same phone sheet, the same rows. What stays here is
 * everything that is Overview's alone: where the regions come from, what a
 * region's address is, and this page's own phone cut-off.
 *
 * Addressing is the part worth naming. Overview makes every region at every
 * depth a URL of its ancestor path, so `/admin/overview/california/southern-california`
 * — a path only has to be unique among siblings, which is a constraint an
 * operator can keep. Venues addresses the same regions by a query parameter
 * against a single globally-unique slug. Neither belongs inside the control,
 * which is why it is handed an href function and told which region is current
 * by ID: the path below is read from OVERVIEW'S OWN rows, not from the shape
 * the picker sees.
 */
import { useMemo } from 'react';

import { usePermissions } from '../../../../hooks/usePermissions';
import { Permission } from '../../../../types/permissions';
import type { ScopeHrefFor } from '../../primitives/scope/scope.types';
import { ScopePicker } from '../../primitives/scope/ScopePicker';

import { OVERVIEW_ALL_REGIONS_HREF } from './overview-all-regions';
import { useAllRegionsOverview } from './overview-api';
import { useOverviewViewport } from './overview-viewport';

export interface OverviewScopePickerProps {
  /** What the current scope is called. */
  label: string;
  /** The region currently being viewed; null at all-regions scope. */
  currentRegionId: string | null;
}

export function OverviewScopePicker({ label, currentRegionId }: OverviewScopePickerProps) {
  const { isPhone } = useOverviewViewport();
  const { hasAnyPermission } = usePermissions();
  // Usually a cache hit: the roster has already run this query, so asking for
  // it again on a region page costs nothing.
  const { data } = useAllRegionsOverview();
  // Memoised, not `data?.regions ?? []` inline: that literal is a new array on
  // every render, which would rebuild the href map below each time and hand the
  // picker a fresh `hrefFor` identity for no reason.
  const regions = useMemo(() => data?.regions ?? [], [data]);

  // A region's Overview address is its ancestor path, top down. The path lives
  // on Overview's row, so it is looked up here by id rather than carried
  // through the picker.
  const hrefFor: ScopeHrefFor = useMemo(() => {
    const pathById = new Map(regions.map((r) => [r.id, r.path]));
    return (region) => {
      // All regions is explicit, so it sticks: the bare root is where a viewer
      // without read:all is sent to their own region.
      if (!region) return OVERVIEW_ALL_REGIONS_HREF;
      const path = pathById.get(region.id);
      return path ? `/admin/overview/${path.join('/')}` : '/admin/overview';
    };
  }, [regions]);

  return (
    <ScopePicker
      label={label}
      currentRegionId={currentRegionId}
      regions={regions}
      hrefFor={hrefFor}
      isPhone={isPhone}
      showAllRegions={hasAnyPermission(Permission.BREADTH_READ_ALL, Permission.READ_ALL)}
    />
  );
}
